import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Brain, Target, TrendingUp, Calendar, CheckCircle2, Clock, BookOpen,
  Zap, Award, ChevronRight, Play, BarChart3, Lightbulb, Loader2,
  Star, Trophy, Sparkles, GraduationCap, Rocket, ArrowRight, ArrowLeft,
  Calculator, BookA, Puzzle, Shapes, RefreshCw, Home, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SixteenWeekCalendar from '../components/SixteenWeekCalendar';
import { useSessionControl } from '../contexts/SessionControlContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// KS2 Subjects with icons and colors
const KS2_SUBJECTS = {
  maths: {
    id: 'maths',
    name: 'Mathematics',
    icon: Calculator,
    color: 'from-blue-600 to-indigo-600',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-400',
    borderColor: 'border-blue-500/30'
  },
  english: {
    id: 'english',
    name: 'English',
    icon: BookA,
    color: 'from-green-600 to-emerald-600',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-400',
    borderColor: 'border-green-500/30'
  },
  verbal_reasoning: {
    id: 'verbal_reasoning',
    name: 'Verbal Reasoning',
    icon: Brain,
    color: 'from-purple-600 to-pink-600',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-400',
    borderColor: 'border-purple-500/30'
  },
  non_verbal_reasoning: {
    id: 'non_verbal_reasoning',
    name: 'Non-Verbal Reasoning',
    icon: Shapes,
    color: 'from-orange-600 to-red-600',
    bgColor: 'bg-orange-500/10',
    textColor: 'text-orange-400',
    borderColor: 'border-orange-500/30'
  }
};

// Mastery level colors
const MASTERY_COLORS = {
  beginner: { bg: 'bg-red-500/20', text: 'text-red-400', badge: 'bg-red-600' },
  developing: { bg: 'bg-orange-500/20', text: 'text-orange-400', badge: 'bg-orange-600' },
  secure: { bg: 'bg-green-500/20', text: 'text-green-400', badge: 'bg-green-600' },
  advanced: { bg: 'bg-blue-500/20', text: 'text-blue-400', badge: 'bg-blue-600' },
  mastery: { bg: 'bg-purple-500/20', text: 'text-purple-400', badge: 'bg-purple-600' }
};

const KS2Assessment = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { startSession, logAnswer, endSession } = useSessionControl();

  // State
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('select'); // select, diagnostic, results, mock
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState(['maths', 'english', 'verbal_reasoning', 'non_verbal_reasoning']); // For multi-select
  
  // Diagnostic state
  const [diagnosticSession, setDiagnosticSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timeStarted, setTimeStarted] = useState(null);
  
  // Results state
  const [results, setResults] = useState(null);
  const [allResults, setAllResults] = useState({});
  const [focusTopics, setFocusTopics] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('wrong'); // 'wrong' | 'all'
  
  // Question counts
  const [questionCounts, setQuestionCounts] = useState(null);

  // Fetch question counts on mount
  useEffect(() => {
    fetchQuestionCounts();
  }, []);

  const fetchQuestionCounts = async () => {
    try {
      const response = await axios.get(`${API}/ks2-11plus/question-counts`);
      setQuestionCounts(response.data.counts);
    } catch (error) {
      console.error('Failed to fetch question counts:', error);
    }
  };

  // Fetch focus topics for 16-week calendar
  const fetchFocusTopics = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Get user ID from token
      let userId = null;
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.sub || payload.user_id;
        } catch {}
      }
      if (!userId) return;
      
      const response = await axios.get(`${API}/learning-engine/growth-zone/${userId}`, { headers });
      if (response.data?.focus_topics) {
        setFocusTopics(response.data.focus_topics);
      }
    } catch (error) {
      console.error('Failed to fetch focus topics:', error);
    }
  };

  // Start diagnostic test
  const startDiagnostic = async (subject) => {
    setLoading(true);
    setSelectedSubject(subject);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/ks2-11plus/diagnostic/start`,
        { subject },
        { headers }
      );
      
      if (response.data.session_id) {
        setDiagnosticSession(response.data);
        // Backend returns question data in 'question' field, not 'current_question'
        setCurrentQuestion({
          ...response.data.question,
          question: response.data.question.text, // Map 'text' to 'question' for consistency
          q_id: response.data.question.q_id
        });
        setQuestionIndex(0);
        setTotalQuestions(response.data.total_questions);
        setTimeStarted(Date.now());
        setSelectedAnswer(null);
        setView('diagnostic');
        toast.success(`Starting ${KS2_SUBJECTS[subject].name} diagnostic!`);
        
        // Start SSCS session for fatigue monitoring
        startSession('diagnostic', subject);
      }
    } catch (error) {
      console.error('Failed to start diagnostic:', error);
      toast.error('Failed to start diagnostic. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Submit answer
  const submitAnswer = async () => {
    if (selectedAnswer === null) {
      toast.error('Please select an answer');
      return;
    }
    
    setLoading(true);
    const timeTaken = Math.round((Date.now() - timeStarted) / 1000);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/ks2-11plus/diagnostic/answer`,
        {
          session_id: diagnosticSession.session_id,
          question_index: questionIndex,
          selected_answer: selectedAnswer,
          time_taken: timeTaken
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.completed) {
        // Log the final answer to SSCS from the results
        const lastResult = data.results.question_results?.[data.results.question_results.length - 1];
        if (lastResult) {
          logAnswer(lastResult.question_id, lastResult.is_correct, timeTaken, String(lastResult.difficulty || 'medium'));
        }
        // End SSCS session
        endSession();
        
        // Diagnostic complete - show results
        setResults({
          overall_score: data.results.overall_score,
          mastery_level: data.results.mastery_level,
          domain_scores: data.results.mastery_matrix || {},
          total_correct: data.results.total_correct,
          total_questions: data.results.total_questions,
          question_results: data.results.question_results || [],
          recommendations: data.results.recommendations || []
        });
        setAllResults(prev => ({
          ...prev,
          [selectedSubject]: {
            overall_score: data.results.overall_score,
            mastery_level: data.results.mastery_level,
            domain_scores: data.results.mastery_matrix || {}
          }
        }));
        setView('results');
        toast.success('Diagnostic complete!');
        
        // Fetch focus topics for 16-week calendar
        fetchFocusTopics();
      } else {
        // Log answer to SSCS for fatigue monitoring
        logAnswer(
          currentQuestion.q_id,
          data.is_correct,
          timeTaken,
          String(currentQuestion.difficulty || 'medium')
        );
        
        // Move to next question
        setCurrentQuestion({
          ...data.question,
          question: data.question.text,
          q_id: data.question.q_id
        });
        setQuestionIndex(prev => prev + 1);
        setSelectedAnswer(null);
        setTimeStarted(Date.now());
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Failed to submit answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate mock exam
  const generateMock = async () => {
    setLoading(true);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const subjects = Object.keys(allResults).filter(s => allResults[s]);
      
      if (subjects.length === 0) {
        toast.error('Please complete at least one diagnostic first');
        setLoading(false);
        return;
      }
      
      const response = await axios.post(
        `${API}/ks2-11plus/mock/generate`,
        {
          subjects,
          question_count: 30,
          time_limit: 45,
          mock_type: 'full'
        },
        { headers }
      );
      
      if (response.data.mock_id) {
        toast.success('Mock exam generated! Starting now...');
        // TODO: Navigate to mock exam page or start inline
      }
    } catch (error) {
      console.error('Failed to generate mock:', error);
      toast.error('Failed to generate mock exam');
    } finally {
      setLoading(false);
    }
  };

  // Render subject selection
  const renderSubjectSelection = () => (
    <div className="space-y-4 md:space-y-6">
      {/* Adaptive Hub Banner */}
      <div className="text-center space-y-2 md:space-y-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30"
        >
          <Zap className="w-5 h-5 text-purple-400" />
          <span className="text-purple-300 font-medium text-sm md:text-base">Adaptive Hub</span>
        </motion.div>
        
        <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-white">
          Choose Your Assessment
        </h1>
        
        <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto px-4">
          Create your personalized learning plan or assess your skills by topic.
        </p>
      </div>

      {/* Two Assessment Options */}
      <div className="max-w-5xl mx-auto px-2 md:px-4 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        
        {/* Option 1: Adaptive Hub */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="relative overflow-hidden bg-gradient-to-br from-purple-900/60 to-indigo-900/60 backdrop-blur border-2 border-purple-500/40 h-full">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-indigo-600/10" />
            
            <CardContent className="p-4 md:p-6 relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg">
                  <Zap className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base md:text-xl font-bold text-white">Adaptive Hub</h3>
                  <p className="text-purple-300 text-xs md:text-sm">Create your personalized learning plan</p>
                </div>
              </div>
              
              <p className="text-gray-300 text-xs md:text-sm mb-4">
                Take a quick assessment and unlock your custom 16-week study path.
              </p>
              
              {/* Subject Selection Checkboxes */}
              <div className="space-y-3 mb-4">
                <p className="text-purple-300 text-xs font-medium">Select subjects:</p>
                <div className="space-y-2">
                  {/* All Subjects Checkbox */}
                  <label 
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                      selectedSubjects.length === 4
                        ? 'bg-purple-500/30 border border-purple-400/50' 
                        : 'bg-purple-900/30 border border-purple-500/20 hover:bg-purple-800/40'
                    }`}
                    data-testid="adaptive-checkbox-all"
                  >
                    <Checkbox 
                      checked={selectedSubjects.length === 4}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedSubjects(['maths', 'english', 'verbal_reasoning', 'non_verbal_reasoning']);
                        } else {
                          setSelectedSubjects([]);
                        }
                      }}
                      className="border-purple-400 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                    />
                    <Zap className="w-4 h-4 text-purple-400" />
                    <span className="text-white font-medium text-sm flex-1">All Subjects</span>
                    <Badge className="bg-purple-500/30 text-purple-300 text-xs">Recommended</Badge>
                  </label>
                  
                  {/* Individual Subject Checkboxes */}
                  {[
                    { id: 'maths', name: 'Maths', icon: Calculator, color: 'blue' },
                    { id: 'english', name: 'English', icon: BookA, color: 'green' },
                    { id: 'verbal_reasoning', name: 'Verbal Reasoning', icon: Brain, color: 'purple' },
                    { id: 'non_verbal_reasoning', name: 'Non-Verbal Reasoning', icon: Shapes, color: 'orange' }
                  ].map((subject) => {
                    const Icon = subject.icon;
                    const isSelected = selectedSubjects.includes(subject.id);
                    return (
                      <label 
                        key={subject.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                          isSelected 
                            ? 'bg-purple-500/30 border border-purple-400/50' 
                            : 'bg-purple-900/30 border border-purple-500/20 hover:bg-purple-800/40'
                        }`}
                        data-testid={`adaptive-checkbox-${subject.id}`}
                      >
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSubjects(prev => [...prev, subject.id]);
                            } else {
                              setSelectedSubjects(prev => prev.filter(s => s !== subject.id));
                            }
                          }}
                          className="border-purple-400 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                        />
                        <Icon className="w-4 h-4 text-purple-400" />
                        <span className="text-white font-medium text-sm">{subject.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              
              {/* Start Assessment Button */}
              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 text-white py-3 disabled:opacity-50"
                disabled={selectedSubjects.length === 0}
                onClick={() => {
                  if (selectedSubjects.length === 0) {
                    toast.error('Please select at least one subject');
                    return;
                  }
                  // Start with first selected subject
                  const firstSubject = selectedSubjects[0];
                  if (selectedSubjects.length > 1) {
                    toast.info(`Starting assessment - ${selectedSubjects.length} subjects selected, beginning with ${firstSubject.replace('_', ' ')}`);
                  }
                  startDiagnostic(firstSubject);
                }}
                data-testid="adaptive-start-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Assessment {selectedSubjects.length > 0 && `(${selectedSubjects.length} subject${selectedSubjects.length > 1 ? 's' : ''})`}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Option 2: Assess by Topic */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="relative overflow-hidden bg-gradient-to-br from-amber-900/60 to-orange-900/60 backdrop-blur border-2 border-amber-500/40 h-full">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-600/10 to-orange-600/10" />
            
            <CardContent className="p-4 md:p-6 relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
                  <Target className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base md:text-xl font-bold text-white">Assess by Topic</h3>
                  <p className="text-amber-300 text-xs md:text-sm">Turn gaps into strengths</p>
                </div>
              </div>
              
              <p className="text-gray-300 text-xs md:text-sm mb-4">
                Complete topic-based assessments to identify your strengths and areas for improvement.
              </p>
              
              {/* 4 Subject Diagnostic Buttons */}
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                {Object.values(KS2_SUBJECTS).map((subject) => {
                  const Icon = subject.icon;
                  const completed = allResults[subject.id];
                  
                  return (
                    <Button
                      key={subject.id}
                      data-testid={`diagnostic-${subject.id}`}
                      variant="outline"
                      className={`h-auto py-3 md:py-4 flex flex-col items-center gap-1 md:gap-2 border-2 transition-all ${
                        completed 
                          ? 'border-green-500/50 bg-green-500/10 hover:bg-green-500/20' 
                          : 'border-amber-500/30 bg-amber-900/30 hover:bg-amber-800/50 hover:border-amber-400/60'
                      }`}
                      onClick={() => !loading && startDiagnostic(subject.id)}
                      disabled={loading}
                    >
                      <Icon className={`w-6 h-6 md:w-8 md:h-8 ${completed ? 'text-green-400' : 'text-amber-400'}`} />
                      <span className={`font-medium text-xs md:text-sm ${completed ? 'text-green-300' : 'text-white'}`}>
                        {subject.id === 'maths' ? 'Maths' : subject.id === 'english' ? 'English' : subject.id === 'verbal_reasoning' ? 'VR' : 'NVR'}
                      </span>
                      {completed ? (
                        <Badge className="bg-green-600 text-white text-[8px] md:text-xs px-1 md:px-2">
                          {Math.round(completed.overall_score)}%
                        </Badge>
                      ) : (
                        <span className="text-[10px] md:text-xs text-amber-300">Start</span>
                      )}
                    </Button>
                  );
                })}
              </div>
              
              {/* Progress and Growth Zone Button */}
              <div className="mt-4 pt-3 border-t border-amber-500/20">
                {Object.keys(allResults).length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-amber-300">
                        {Object.keys(allResults).length}/4 subjects assessed
                      </p>
                      <div className="flex gap-1">
                        {Object.values(KS2_SUBJECTS).map((subject) => (
                          <div
                            key={subject.id}
                            className={`w-2 h-2 rounded-full ${
                              allResults[subject.id] ? 'bg-green-500' : 'bg-amber-500/30'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <Button
                      data-testid="go-to-growth-zone-btn"
                      onClick={() => navigate('/ai-practice')}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90 text-white"
                    >
                      <Rocket className="w-4 h-4 mr-2" />
                      Go to My Growth Zone
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-center text-amber-300/70">
                    Complete at least one assessment to unlock My Growth Zone
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );

  // Render diagnostic question
  const renderDiagnostic = () => {
    if (!currentQuestion) return null;
    
    const subject = KS2_SUBJECTS[selectedSubject];
    const Icon = subject.icon;
    const progress = ((questionIndex + 1) / totalQuestions) * 100;
    
    // Subject colors for light theme
    const subjectColors = {
      maths: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', accent: 'bg-blue-600' },
      english: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', accent: 'bg-green-600' },
      verbal_reasoning: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', accent: 'bg-purple-600' },
      non_verbal_reasoning: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', accent: 'bg-orange-600' }
    };
    const colors = subjectColors[selectedSubject] || subjectColors.maths;
    
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Sticky Header */}
        <div className="bg-white border-b border-gray-200 py-3 px-4 sticky top-0 z-10 shadow-sm">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => {
                if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
                  endSession();
                  setView('select');
                  setDiagnosticSession(null);
                }
              }}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Exit
            </Button>
            
            <div className="flex items-center gap-3">
              <Badge className={`${colors.bg} ${colors.text} ${colors.border}`}>
                <Icon className="w-3 h-3 mr-1" />
                {subject.name}
              </Badge>
              <span className="text-gray-600 text-sm">
                Q {questionIndex + 1} of {totalQuestions}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
              <Progress value={progress} className="w-20 h-2" />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Question Card */}
          <motion.div
            key={currentQuestion.q_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-start gap-4">
                  <div className={`${colors.accent} text-white rounded-lg w-12 h-12 flex items-center justify-center font-bold text-lg shrink-0`}>
                    Q{questionIndex + 1}
                  </div>
                  <div className="flex-1">
                    {currentQuestion.domain && (
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className={`${colors.bg} ${colors.text} ${colors.border}`}>
                          {currentQuestion.domain.replace(/_/g, ' ')}
                        </Badge>
                        {currentQuestion.difficulty && (
                          <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                            Difficulty: {currentQuestion.difficulty}/5
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Question Text */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <p className="text-gray-900 text-lg leading-relaxed">
                    {currentQuestion.question}
                  </p>
                </div>

                {/* Options */}
                <RadioGroup
                  value={selectedAnswer?.toString()}
                  onValueChange={(value) => setSelectedAnswer(parseInt(value))}
                  className="space-y-3"
                >
                  {currentQuestion.options.map((option, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Label
                        data-testid={`answer-option-${index}`}
                        htmlFor={`option-${index}`}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                          selectedAnswer === index
                            ? `${colors.border} ${colors.bg}`
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                        }`}
                      >
                        <RadioGroupItem
                          value={index.toString()}
                          id={`option-${index}`}
                          className="shrink-0"
                        />
                        <span className={`text-lg font-semibold mr-2 ${selectedAnswer === index ? colors.text : 'text-gray-500'}`}>
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <span className={`text-base ${selectedAnswer === index ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                          {option}
                        </span>
                      </Label>
                    </motion.div>
                  ))}
                </RadioGroup>

                {/* Submit Button */}
                <div className="pt-4 border-t border-gray-200">
                  <Button
                    data-testid="submit-answer-btn"
                    onClick={submitAnswer}
                    disabled={selectedAnswer === null || loading}
                    className={`w-full ${colors.accent} hover:opacity-90 text-white`}
                    size="lg"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : questionIndex === totalQuestions - 1 ? (
                      <>
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        Complete Diagnostic
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-5 h-5 mr-2" />
                        Next Question
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  };

  // Render results
  const renderResults = () => {
    if (!results) return null;
    
    const subject = KS2_SUBJECTS[selectedSubject];
    const Icon = subject.icon;
    
    // Subject colors for light theme
    const subjectColors = {
      maths: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', accent: 'bg-blue-600' },
      english: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', accent: 'bg-green-600' },
      verbal_reasoning: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', accent: 'bg-purple-600' },
      non_verbal_reasoning: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', accent: 'bg-orange-600' }
    };
    const colors = subjectColors[selectedSubject] || subjectColors.maths;
    
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 space-y-6">
          {/* Header */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center space-y-4"
          >
            <div className={`inline-flex p-4 rounded-full ${colors.bg}`}>
              <Icon className={`w-12 h-12 ${colors.text}`} />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900">
              {subject.name} Results
            </h1>
            
            <p className="text-gray-600">
              Great work completing the diagnostic assessment!
            </p>
          </motion.div>

          {/* Score Card */}
          <Card className="bg-white border-gray-200 shadow-sm overflow-hidden">
            <div className={`h-2 ${colors.accent}`} />
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-gray-200"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${results.overall_score * 3.52} 352`}
                      strokeLinecap="round"
                      className={colors.text}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className="text-4xl font-bold text-gray-900">
                      {Math.round(results.overall_score)}%
                    </span>
                    <span className="text-sm text-gray-500">Score</span>
                  </div>
                </div>
                
                <Badge className={`${
                  results.overall_score >= 80 ? 'bg-green-100 text-green-700 border-green-200' :
                  results.overall_score >= 60 ? 'bg-blue-100 text-blue-700 border-blue-200' :
                  results.overall_score >= 40 ? 'bg-amber-100 text-amber-700 border-amber-200' :
                  'bg-red-100 text-red-700 border-red-200'
                } px-4 py-1 text-base`}>
                  {results.mastery_level?.charAt(0).toUpperCase() + results.mastery_level?.slice(1)} Level
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Domain Breakdown */}
          {results.domain_scores && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-gray-600" />
                  Performance by Topic
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(results.domain_scores).map(([domain, score]) => (
                  <div key={domain} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 capitalize">
                        {domain.replace(/_/g, ' ')}
                      </span>
                      <span className={`text-sm font-medium ${
                        score >= 80 ? 'text-green-600' : 
                        score >= 60 ? 'text-blue-600' : 
                        score >= 40 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {Math.round(score)}%
                      </span>
                    </div>
                    <Progress value={score} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Review Answers Section */}
          {results.question_results && results.question_results.length > 0 && (
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="review-answers-section">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-500" />
                    Review Your Answers
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReview(!showReview)}
                    className="text-sm"
                    data-testid="toggle-review-btn"
                  >
                    {showReview ? 'Hide Review' : 'Show Review'}
                    <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showReview ? 'rotate-90' : ''}`} />
                  </Button>
                </div>
                {!showReview && (
                  <p className="text-sm text-gray-500 mt-1">
                    {results.question_results.filter(q => !q.is_correct).length} wrong out of {results.question_results.length} questions
                  </p>
                )}
              </CardHeader>
              
              {showReview && (
                <CardContent className="space-y-4">
                  {/* Filter tabs */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      variant={reviewFilter === 'wrong' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setReviewFilter('wrong')}
                      className={reviewFilter === 'wrong' ? 'bg-red-600 hover:bg-red-700' : ''}
                      data-testid="filter-wrong-btn"
                    >
                      Wrong Answers ({results.question_results.filter(q => !q.is_correct).length})
                    </Button>
                    <Button
                      variant={reviewFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setReviewFilter('all')}
                      data-testid="filter-all-btn"
                    >
                      All Questions ({results.question_results.length})
                    </Button>
                  </div>

                  {/* Question Review Cards */}
                  {results.question_results
                    .filter(q => reviewFilter === 'all' || !q.is_correct)
                    .map((q, idx) => (
                    <motion.div
                      key={q.question_id || idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className={`p-4 rounded-lg border ${
                        q.is_correct 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                      data-testid={`review-question-${idx}`}
                    >
                      {/* Question header */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                          q.is_correct ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          {q.is_correct 
                            ? <CheckCircle2 className="w-4 h-4 text-white" /> 
                            : <X className="w-4 h-4 text-white" />
                          }
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{q.question_text}</p>
                          <Badge variant="outline" className="mt-1 text-xs capitalize">
                            {(q.domain || '').replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      </div>

                      {/* Options */}
                      <div className="ml-10 space-y-1.5">
                        {(q.options || []).map((option, optIdx) => {
                          const isUserAnswer = optIdx === q.user_answer;
                          const isCorrectAnswer = optIdx === q.correct_answer;
                          
                          let optionStyle = 'bg-white border-gray-200 text-gray-600';
                          if (isCorrectAnswer) {
                            optionStyle = 'bg-green-100 border-green-400 text-green-800 font-medium';
                          } else if (isUserAnswer && !q.is_correct) {
                            optionStyle = 'bg-red-100 border-red-400 text-red-700 line-through';
                          }
                          
                          return (
                            <div
                              key={optIdx}
                              className={`px-3 py-1.5 rounded-md border text-sm flex items-center gap-2 ${optionStyle}`}
                            >
                              <span className="w-5 h-5 rounded-full border flex items-center justify-center text-xs flex-shrink-0">
                                {String.fromCharCode(65 + optIdx)}
                              </span>
                              <span>{option}</span>
                              {isCorrectAnswer && (
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-600 ml-auto flex-shrink-0" />
                              )}
                              {isUserAnswer && !q.is_correct && (
                                <X className="w-3.5 h-3.5 text-red-500 ml-auto flex-shrink-0" />
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Explanation */}
                      {q.explanation && !q.is_correct && (
                        <div className="ml-10 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <p className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
                            <Lightbulb className="w-3.5 h-3.5" />
                            Explanation
                          </p>
                          <p className="text-sm text-amber-700 mt-1">{q.explanation}</p>
                        </div>
                      )}
                    </motion.div>
                  ))}

                  {reviewFilter === 'wrong' && results.question_results.filter(q => !q.is_correct).length === 0 && (
                    <div className="text-center py-8 text-green-600">
                      <Trophy className="w-10 h-10 mx-auto mb-2" />
                      <p className="font-medium">Perfect score! All answers were correct.</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* 16-Week Learning Calendar - Embedded */}
          <div className="mt-6">
            <SixteenWeekCalendar
              focusTopics={focusTopics}
              weeklyPlan={[]}
              onRefresh={fetchFocusTopics}
              showHeader={true}
              compact={false}
            />
          </div>

          {/* Actions */}
          <div className="space-y-3 mt-6">
            {/* Go to Growth Zone Button */}
            <Button
              data-testid="go-to-growth-zone-from-results-btn"
              onClick={() => navigate('/ai-practice')}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90 text-white"
              size="lg"
            >
              <Rocket className="w-5 h-5 mr-2" />
              Practice Weak Topics in My Growth Zone
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <div className="flex gap-4">
              <Button
                data-testid="back-to-subjects-btn"
                variant="outline"
                onClick={() => setView('select')}
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Subjects
              </Button>
              
              <Button
                data-testid="retake-diagnostic-btn"
                onClick={() => startDiagnostic(selectedSubject)}
                className={`flex-1 ${colors.accent} text-white hover:opacity-90`}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retake Test
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="ks2-assessment-page" className={`min-h-screen ${view === 'select' ? 'bg-gray-950' : 'bg-gray-50'}`}>
      {/* Background - only show for select view */}
      {view === 'select' && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        </div>
      )}

      {/* Navigation - only show for select view */}
      {view === 'select' && (
        <nav className="sticky top-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
              
              <div className="flex items-center gap-2">
                <GraduationCap className="w-6 h-6 text-amber-400" />
                <span className="font-bold text-white">KS2 11+ Assessment</span>
              </div>
              
              <div className="w-24" /> {/* Spacer */}
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className={`relative ${view === 'select' ? 'max-w-7xl mx-auto px-4 py-8' : ''}`}>
        <AnimatePresence mode="wait">
          {view === 'select' && (
            <motion.div
              key="select"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {renderSubjectSelection()}
            </motion.div>
          )}
          
          {view === 'diagnostic' && (
            <motion.div
              key="diagnostic"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {renderDiagnostic()}
            </motion.div>
          )}
          
          {view === 'results' && (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {renderResults()}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default KS2Assessment;
